import React, { useState, useEffect, useCallback } from "react";
import ActionDropdown from "../common/ui/actiondropdown";
import AddCustomer from "./addcustomer";
import Customerheading from "./customerheading";
import { refreshAccessToken, API_CONFIG, getHeaders } from "../../config/api";
import { useLoader } from "../../context/loadercontext";
import Pagination from "../common/table/tablepagination";
import { DeletePreviewModal, FinalDeleteConfirmModal } from "../common/ui/deletemodal";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext"; // Auth context
import NoFound from "../common/ui/NoFound";
import WarningModal from "../common/ui/warningmodel";

export default function Viewcustomer() {
  const [customers, setCustomers] = useState([]);
  const { setLoading } = useLoader();

  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [showDeletePreview, setShowDeletePreview] = useState(false); // 🆕 first modal
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false); // 🆕 second modal
  const [deleteIndex, setDeleteIndex] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const { logout } = useAuth();

  // ✅ Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const handleRowToggle = (index) => {
    setOpenRowIndex(openRowIndex === index ? null : index);
  };

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    const startTime = Date.now();

    try {
      // FIRST API CALL
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.GET_ALL}`,
        { method: "GET", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.GET_ALL}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }

      const result = await response.json();

      if (result.status === 200) {
        const mappedData = result.data.map((s) => ({
          id: s.id,
          customerName: s.name,
          customerAddress: s.address,
          createdAt: s.created_at,
        }));
        setCustomers(mappedData);
      } else {
        console.error("Failed to fetch customers:", result.message);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      const elapsed = Date.now() - startTime;
      const remainingDelay = Math.max(0, 1000 - elapsed);
      setTimeout(() => setLoading(false), remainingDelay);
    }
  }, [setLoading, logout]);


  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleEdit = (index) => {
    setEditData({ ...customers[index], index });
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditData(null);
  };

  // 🆕 Step 1: Open preview modal before delete confirmation
  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setShowDeletePreview(true);
  };

  // 🆕 Step 2: After user confirms preview, show final confirmation modal
  const handleProceedToFinalConfirm = () => {
    setShowDeletePreview(false);
    setShowFinalDeleteConfirm(true);
  };

  const handleCancelPreview = () => {
    setShowDeletePreview(false);
    setDeleteIndex(null);
  };

  // 🧾 Final confirmation — perform delete API
  const handleConfirmDelete = async () => {
    try {
      const customerId = customers[deleteIndex]?.id;
      if (!customerId) return;
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.SOFT_DELETE(customerId)}`,
        { method: "PUT", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.SOFT_DELETE(customerId)}`,
            { method: "PUT", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }


      const result = await response.json();

      if (response.ok && (result.status === 200 || result.message?.includes("deleted"))) {
        toast.success("Customer deleted successfully");
        await fetchCustomers();
      } else {
        setWarningMessage(result.message || "Failed to delete customer");
        setWarningVisible(true);
        console.error("Failed to delete customer:", result);
      }
    } catch (error) {
      console.error("Error deleting customer:", error);
      setWarningMessage("Error deleting customer");
      setWarningVisible(true);
    } finally {
      setShowFinalDeleteConfirm(false);
      setDeleteIndex(null);
    }
  };

  // ✅ Pagination logic
  const totalPages = Math.ceil(customers.length / rowsPerPage);
  const paginatedData = customers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  //Format Date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <>
      <Customerheading onAddClick={() => setShowAddModal(true)} />

      <div className="mt-6 overflow-x-auto">
        <table className="min-w-[950px] w-full text-table text-center">
          <thead className="border-b border-gray-200 text-gray-600 font-medium">
            <tr>
              <th className="px-4 py-3"></th>
              <th className="px-4 py-3">Customer Name</th>
              <th className="px-4 py-3">Customer Address</th>
              <th className="px-4 py-3">Created At</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? ( // ✅ FIXED: Use paginatedData here
              paginatedData.map((row, idx) => (
                <tr
                  key={row.id || idx}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowToggle(idx)}
                >
                  <td className="px-3 py-1">
                    <div className="flex justify-center mt-1">
                      {(() => {
                        const colorPairs = [
                          { bg: "#edeff1", text: "#1c2a3d" },
                          { bg: "#dbf6e5", text: "#25915a" },
                          { bg: "#fff1d6", text: "#ce880d" },
                          { bg: "#ffe4de", text: "#b71d18" },
                          { bg: "#d6f4f9", text: "#1a7da7" },
                        ];

                        const randomColor =
                          colorPairs[Math.floor(Math.random() * colorPairs.length)];

                        const nameParts = row.customerName
                          ? row.customerName.trim().split(" ")
                          : [];
                        let initials = "";
                        if (nameParts.length > 1) {
                          initials = (
                            nameParts[0].charAt(0) + nameParts[1].charAt(0)
                          ).toUpperCase();
                        } else if (nameParts.length === 1) {
                          initials = nameParts[0].charAt(0).toUpperCase();
                        } else {
                          initials = "-";
                        }

                        return (
                          <div
                            className="w-10 h-6 flex items-center justify-center font-semibold text-xs text-center rounded uppercase shadow-sm"
                            style={{
                              backgroundColor: randomColor.bg,
                              color: randomColor.text,
                            }}
                          >
                            {initials}
                          </div>
                        );
                      })()}
                    </div>
                  </td>

                  <td className="px-3 py-1">
                    {row.customerName
                      ? row.customerName
                        .toLowerCase()
                        .split(" ")
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ")
                      : ""}
                  </td>
                  <td className="px-3 py-1 whitespace-normal break-words max-w-[250px] ">
                    {row.customerAddress
                      ? row.customerAddress
                        .toLowerCase()
                        .split(",") // split by comma
                        .map(part =>
                          part
                            .trim()
                            .split(" ")
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(" ")
                        )
                        .join(", ") // ensure comma + space
                      : ""
                    }

                  </td>

                  <td className="px-3 py-1">{formatDate(row.createdAt)}</td>
                  <td
                    className="px-3 py-1 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionDropdown
                      actions={{ edit: true, delete: true }}
                      onEdit={() =>
                        handleEdit((currentPage - 1) * rowsPerPage + idx)
                      } // ✅ Adjust index to global index
                      onDelete={() =>
                        handleDeleteClick((currentPage - 1) * rowsPerPage + idx)
                      } // ✅ same fix
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="py-4 text-gray-500">
                  <NoFound />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ Pagination */}
      {customers.length > rowsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          totalItems={customers.length}
        />
      )}

      {/* Add Modal */}
      <AddCustomer
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCustomerAdded={fetchCustomers}
      />

      {/* Edit Modal */}
      <AddCustomer
        isOpen={showEditModal}
        onClose={handleCloseEdit}
        editData={editData}
        onCustomerAdded={fetchCustomers}
      />

      {/* 🧩 Reusable Modals */}
      {showDeletePreview && deleteIndex !== null && (
        <DeletePreviewModal
          title="Customer Details"
          data={{
            Name: customers[deleteIndex]?.customerName ? customers[deleteIndex].customerName.toLowerCase().split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ") : "",
            Address: customers[deleteIndex]?.customerAddress
              ? customers[deleteIndex].customerAddress
                .toLowerCase()
                .split(",") // split by comma only
                .map(part =>
                  part
                    .trim()
                    .split(" ")
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                )
                .join(", ") // add comma + space back
              : "",
            "Created At": formatDate(customers[deleteIndex]?.createdAt),
          }}
          onCancel={handleCancelPreview}
          onProceed={handleProceedToFinalConfirm}
        />
      )}

      {showFinalDeleteConfirm && (
        <FinalDeleteConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to permanently delete this customer?"
          onCancel={() => setShowFinalDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}
